'use client';

import { c as _c } from "react/compiler-runtime";
import { IconLayer } from '@deck.gl/layers';
import { MapboxOverlay } from '@deck.gl/mapbox';
import { MARKER_HEIGHT } from '@repo/types/maps/map-marker';
import type { MapData, MapDataLocationFeature, MapIconFonts } from '@repo/types/maps/map-types';
import type * as MapLibre from 'maplibre-gl';
import { useCallback, useEffect, useRef } from 'react';
import { getMarkerImageId, loadMarkerImageData, type MarkerImageData, type MarkerImageRequest, normalizeMarkerVerticalLevel } from '../utils/marker-image';
import { MARKER_IMAGE_HIGHLIGHTED_PREFIX, MARKER_IMAGE_PREFIX } from './constants';
const ATLAS_PADDING_PX = 2;
const ATLAS_MAX_WIDTH_PX = 4096;
const SELECTED_MARKER_SCALE = 1.5;
const HOVERED_MARKER_SCALE = 1.2;
interface IconMappingEntry {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly anchorX: number;
  readonly anchorY: number;
  readonly mask: false;
}
type IconMapping = Record<string, IconMappingEntry>;
interface DenseMarkerDeckDatum {
  readonly feature: MapDataLocationFeature;
  readonly iconId: string;
  readonly size: number;
}
interface DenseMarkerLayerOverlayProps {
  readonly beforeId?: string;
}
type DenseMarkerIconLayer = IconLayer<DenseMarkerDeckDatum, DenseMarkerLayerOverlayProps>;
interface DenseMarkerIconRequest {
  readonly request: MarkerImageRequest;
  readonly size: number;
}
interface DenseMarkerAtlas {
  readonly iconAtlas: string;
  readonly iconMapping: IconMapping;
}
interface UseDenseMarkerDeckRendererOptions {
  readonly enabled: boolean;
  readonly map: MapLibre.Map | null;
  readonly mapData: MapData | null;
  readonly locations: readonly MapDataLocationFeature[];
  readonly fonts: MapIconFonts | null;
  readonly highlightedPinIds: readonly string[];
  readonly hoveredMarkerId: string | null;
  readonly selectedMarkerId: string | null;
  readonly onReadyChange?: (ready: boolean) => void;
}
function removeOverlay(map: MapLibre.Map | null, overlay: MapboxOverlay) {
  let removed = false;
  if (map) {
    try {
      map.removeControl(overlay);
      removed = true;
    } catch {
      removed = false;
    }
  }
  if (!removed) {
    overlay.finalize();
  }
}
function getIconRequest({
  feature,
  mapData,
  fonts,
  highlighted,
  selected = false
}: {
  readonly feature: MapDataLocationFeature;
  readonly mapData: MapData;
  readonly fonts: MapIconFonts;
  readonly highlighted: boolean;
  readonly selected?: boolean;
}): DenseMarkerIconRequest {
  const verticalLevel = normalizeMarkerVerticalLevel(feature.properties.verticalLevel);
  const color = feature.properties.color ?? 'default';
  const subcategoryExternalId = feature.properties.subcategoryExternalId ?? '';
  const found = feature.properties.found === true;
  return {
    request: {
      id: getMarkerImageId({
        prefix: highlighted ? MARKER_IMAGE_HIGHLIGHTED_PREFIX : MARKER_IMAGE_PREFIX,
        color,
        subcategoryExternalId,
        found,
        selected,
        verticalLevel
      }),
      color,
      subcategoryExternalId,
      found,
      selected,
      supportedFonts: fonts,
      mapData,
      verticalLevel,
      highlighted
    },
    size: MARKER_HEIGHT * (selected ? SELECTED_MARKER_SCALE : 1)
  };
}
function toDeckDatum(feature: MapDataLocationFeature, iconRequest: DenseMarkerIconRequest): DenseMarkerDeckDatum {
  return {
    feature,
    iconId: iconRequest.request.id,
    size: iconRequest.size
  };
}
function getPinInsertBeforeLayerId(map: MapLibre.Map) {
  return map.getLayer('markers') ? 'markers' : undefined;
}
async function buildIconAtlas(requests: Iterable<DenseMarkerIconRequest>): Promise<DenseMarkerAtlas> {
  const requestsById = new Map<string, MarkerImageRequest>();
  for (const {
    request
  } of requests) {
    if (!requestsById.has(request.id)) {
      requestsById.set(request.id, request);
    }
  }
  const imageEntries = await Promise.all([...requestsById].map(async ([id, request]) => [id, await loadMarkerImageData(request)] as const));
  const imagesById = new Map<string, MarkerImageData>(imageEntries);
  let x = 0;
  let y = 0;
  let rowHeight = 0;
  let atlasWidth = 1;
  const iconMapping: IconMapping = {};
  for (const [id, imageData] of imagesById) {
    if (x > 0 && x + imageData.image.width > ATLAS_MAX_WIDTH_PX) {
      x = 0;
      y += rowHeight + ATLAS_PADDING_PX;
      rowHeight = 0;
    }
    iconMapping[id] = {
      x,
      y,
      width: imageData.image.width,
      height: imageData.image.height,
      anchorX: imageData.image.width / 2,
      anchorY: imageData.image.height / 2,
      mask: false
    };
    x += imageData.image.width + ATLAS_PADDING_PX;
    rowHeight = Math.max(rowHeight, imageData.image.height);
    atlasWidth = Math.max(atlasWidth, x);
  }
  const atlasHeight = Math.max(1, y + rowHeight);
  const canvas = document.createElement('canvas');
  canvas.width = atlasWidth;
  canvas.height = atlasHeight;
  const context = canvas.getContext('2d');
  if (!context) {
    return {
      iconAtlas: '',
      iconMapping: {}
    };
  }
  context.clearRect(0, 0, atlasWidth, atlasHeight);
  for (const [id, imageData] of imagesById) {
    const mapping = iconMapping[id];
    if (!mapping) continue;
    context.drawImage(imageData.image, mapping.x, mapping.y, mapping.width, mapping.height);
  }
  return {
    iconAtlas: canvas.toDataURL('image/png'),
    iconMapping
  };
}
export function useDenseMarkerDeckRenderer(t0) {
  const $ = _c(40);
  const {
    enabled,
    map,
    mapData,
    locations,
    fonts,
    highlightedPinIds,
    hoveredMarkerId,
    selectedMarkerId,
    onReadyChange
  } = t0;
  const overlayRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const baseLayersRef = useRef(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const hoveredLayersRef = useRef(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const selectedLayersRef = useRef(t3);
  const onReadyChangeRef = useRef(onReadyChange);
  let t4;
  let t5;
  if ($[3] !== onReadyChange) {
    t4 = () => {
      onReadyChangeRef.current = onReadyChange;
    };
    t5 = [onReadyChange];
    $[3] = onReadyChange;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = ready => {
      onReadyChangeRef.current?.(ready);
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const setReady = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      overlayRef.current?.setProps({
        layers: [...baseLayersRef.current, ...hoveredLayersRef.current, ...selectedLayersRef.current]
      });
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const applyLayers = t7;
  let t8;
  let t9;
  if ($[8] !== enabled || $[9] !== map) {
    t8 = () => {
      if (!enabled || !map) {
        if (overlayRef.current) {
          removeOverlay(map, overlayRef.current);
        }
        overlayRef.current = null;
        baseLayersRef.current = [];
        hoveredLayersRef.current = [];
        selectedLayersRef.current = [];
        setReady(false);
        return;
      }
      const overlay = new MapboxOverlay({
        interleaved: true,
        layers: []
      });
      overlayRef.current = overlay;
      map.addControl(overlay);
      applyLayers();
      return () => {
        removeOverlay(map, overlay);
        if (overlayRef.current === overlay) {
          overlayRef.current = null;
        }
      };
    };
    t9 = [applyLayers, enabled, map, setReady];
    $[8] = enabled;
    $[9] = map;
    $[10] = t8;
    $[11] = t9;
  } else {
    t8 = $[10];
    t9 = $[11];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[12] !== enabled || $[13] !== fonts || $[14] !== highlightedPinIds || $[15] !== locations || $[16] !== map || $[17] !== mapData || $[18] !== selectedMarkerId) {
    t10 = () => {
      const overlay_0 = overlayRef.current;
      if (!enabled || !overlay_0 || !mapData || !fonts) {
        baseLayersRef.current = [];
        applyLayers();
        setReady(false);
        return;
      }
      const highlightedIds = new Set(highlightedPinIds);
      const normalData = [];
      const highlightedData = [];
      const iconRequests = new Map();
      const beforeId = map ? getPinInsertBeforeLayerId(map) : undefined;
      for (const feature of locations) {
        if (feature.geometry.type !== "Point") {
          continue;
        }
        if (feature.properties.externalId === selectedMarkerId) {
          continue;
        }
        const highlighted = highlightedIds.has(feature.properties.externalId);
        const iconRequest = getIconRequest({
          feature,
          mapData,
          fonts,
          highlighted
        });
        iconRequests.set(iconRequest.request.id, iconRequest);
        const datum = toDeckDatum(feature, iconRequest);
        if (highlighted) {
          highlightedData.push(datum);
        } else {
          normalData.push(datum);
        }
      }
      let cancelled = false;
      buildIconAtlas(iconRequests.values()).then(t12 => {
        const {
          iconAtlas,
          iconMapping
        } = t12;
        if (cancelled) {
          return;
        }
        const sharedLayerProps = {
          iconAtlas,
          iconMapping,
          pickable: false,
          sizeUnits: "pixels" as const,
          sizeBasis: "height" as const,
          alphaCutoff: 0.05,
          getPosition: _temp,
          getIcon: _temp2,
          getSize: _temp3
        };
        baseLayersRef.current = [new IconLayer({
          ...sharedLayerProps,
          id: "dense-map-markers",
          data: normalData,
          beforeId
        }), new IconLayer({
          ...sharedLayerProps,
          id: "dense-map-markers-highlighted",
          data: highlightedData,
          beforeId
        })];
        applyLayers();
        setReady(true);
      });
      return () => {
        cancelled = true;
      };
    };
    t11 = [applyLayers, enabled, fonts, highlightedPinIds, locations, map, mapData, selectedMarkerId, setReady];
    $[12] = enabled;
    $[13] = fonts;
    $[14] = highlightedPinIds;
    $[15] = locations;
    $[16] = map;
    $[17] = mapData;
    $[18] = selectedMarkerId;
    $[19] = t10;
    $[20] = t11;
  } else {
    t10 = $[19];
    t11 = $[20];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[21] !== enabled || $[22] !== fonts || $[23] !== highlightedPinIds || $[24] !== locations || $[25] !== map || $[26] !== mapData || $[27] !== selectedMarkerId) {
    t12 = () => {
      if (!enabled || !mapData || !fonts || !selectedMarkerId) {
        selectedLayersRef.current = [];
        applyLayers();
        return;
      }
      const selectedFeature = locations.find(feature_0 => feature_0.properties.externalId === selectedMarkerId);
      if (!selectedFeature) {
        selectedLayersRef.current = [];
        applyLayers();
        return;
      }
      const selectedIconRequest = getIconRequest({
        feature: selectedFeature,
        mapData,
        fonts,
        highlighted: highlightedPinIds.includes(selectedMarkerId),
        selected: true
      });
      const selectedData = [toDeckDatum(selectedFeature, selectedIconRequest)];
      const beforeId_0 = map ? getPinInsertBeforeLayerId(map) : undefined;
      let cancelled_0 = false;
      buildIconAtlas([selectedIconRequest]).then(t14 => {
        const {
          iconAtlas: iconAtlas_0,
          iconMapping: iconMapping_0
        } = t14;
        if (cancelled_0) {
          return;
        }
        selectedLayersRef.current = [new IconLayer({
          id: "dense-map-marker-selected",
          data: selectedData,
          iconAtlas: iconAtlas_0,
          iconMapping: iconMapping_0,
          beforeId: beforeId_0,
          pickable: false,
          sizeUnits: "pixels",
          sizeBasis: "height",
          alphaCutoff: 0.05,
          getPosition: _temp4,
          getIcon: _temp5,
          getSize: _temp6
        })];
        applyLayers();
      });
      return () => {
        cancelled_0 = true;
      };
    };
    t13 = [applyLayers, enabled, fonts, highlightedPinIds, locations, map, mapData, selectedMarkerId];
    $[21] = enabled;
    $[22] = fonts;
    $[23] = highlightedPinIds;
    $[24] = locations;
    $[25] = map;
    $[26] = mapData;
    $[27] = selectedMarkerId;
    $[28] = t12;
    $[29] = t13;
  } else {
    t12 = $[28];
    t13 = $[29];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[30] !== enabled || $[31] !== fonts || $[32] !== highlightedPinIds || $[33] !== hoveredMarkerId || $[34] !== locations || $[35] !== map || $[36] !== mapData || $[37] !== selectedMarkerId) {
    t14 = () => {
      if (!enabled || !mapData || !fonts || !hoveredMarkerId || hoveredMarkerId === selectedMarkerId) {
        hoveredLayersRef.current = [];
        applyLayers();
        return;
      }
      const hoveredFeature = locations.find(feature_1 => feature_1.properties.externalId === hoveredMarkerId);
      if (!hoveredFeature) {
        hoveredLayersRef.current = [];
        applyLayers();
        return;
      }
      const hoveredIconRequest = getIconRequest({
        feature: hoveredFeature,
        mapData,
        fonts,
        highlighted: highlightedPinIds.includes(hoveredMarkerId)
      });
      const hoveredData = [{
        ...toDeckDatum(hoveredFeature, hoveredIconRequest),
        size: MARKER_HEIGHT * HOVERED_MARKER_SCALE
      }];
      const beforeId_1 = map ? getPinInsertBeforeLayerId(map) : undefined;
      let cancelled_1 = false;
      buildIconAtlas([hoveredIconRequest]).then(t16 => {
        const {
          iconAtlas: iconAtlas_1,
          iconMapping: iconMapping_1
        } = t16;
        if (cancelled_1) {
          return;
        }
        hoveredLayersRef.current = [new IconLayer({
          id: "dense-map-marker-hovered",
          data: hoveredData,
          iconAtlas: iconAtlas_1,
          iconMapping: iconMapping_1,
          beforeId: beforeId_1,
          pickable: false,
          sizeUnits: "pixels",
          sizeBasis: "height",
          alphaCutoff: 0.05,
          getPosition: _temp7,
          getIcon: _temp8,
          getSize: _temp9
        })];
        applyLayers();
      });
      return () => {
        cancelled_1 = true;
      };
    };
    t15 = [applyLayers, enabled, fonts, highlightedPinIds, hoveredMarkerId, locations, map, mapData, selectedMarkerId];
    $[30] = enabled;
    $[31] = fonts;
    $[32] = highlightedPinIds;
    $[33] = hoveredMarkerId;
    $[34] = locations;
    $[35] = map;
    $[36] = mapData;
    $[37] = selectedMarkerId;
    $[38] = t14;
    $[39] = t15;
  } else {
    t14 = $[38];
    t15 = $[39];
  }
  useEffect(t14, t15);
}
function _temp9(datum_8) {
  return datum_8.size;
}
function _temp8(datum_7) {
  return datum_7.iconId;
}
function _temp7(datum_6) {
  return datum_6.feature.geometry.coordinates;
}
function _temp6(datum_5) {
  return datum_5.size;
}
function _temp5(datum_4) {
  return datum_4.iconId;
}
function _temp4(datum_3) {
  return datum_3.feature.geometry.coordinates;
}
function _temp3(datum_2) {
  return datum_2.size;
}
function _temp2(datum_1) {
  return datum_1.iconId;
}
function _temp(datum_0) {
  return datum_0.feature.geometry.coordinates;
}